import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const SITE = (import.meta.env.VITE_SITE_URL || (typeof window !== "undefined" ? window.location.origin : "")).replace(/\/$/, "");

/** Canonical URL + og:url for public pages (query strings dropped except pagination-free filters). */
export function CanonicalLink() {
  const { pathname } = useLocation();
  const href = `${SITE}${pathname === "/" ? "" : pathname}` || SITE;
  return (
    <Helmet>
      <link rel="canonical" href={href} />
      <meta property="og:url" content={href} />
    </Helmet>
  );
}

const DEFAULT_DESCRIPTION =
  "Nigeria's safe marketplace. Buy and sell phones, cars, homes, solar, jobs and services. Chat with sellers and pay with RealitySafe: your money is held until you get your item.";

/** Absolute URL for OpenGraph images (social crawlers ignore relative URLs). */
function absolute(url: string): string {
  return /^https?:\/\//.test(url) ? url : `${SITE}${url.startsWith("/") ? "" : "/"}${url}`;
}

export interface PageMetaProps {
  /** Full document title, e.g. "Phones · RealityStore". */
  title: string;
  description?: string;
  /** Share image; defaults to the site card. */
  image?: string;
  type?: "website" | "product" | "profile" | "article";
}

/** Title, description, OpenGraph and Twitter card for one page. Deeper instances override shallower ones. */
export function PageMeta({ title, description = DEFAULT_DESCRIPTION, image = "/og-image.jpg", type = "website" }: PageMetaProps) {
  const text = description.replace(/\s+/g, " ").trim().slice(0, 200);
  const shareTitle = title.replace(/ · RealityStore$/, "");
  const img = absolute(image);
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={text} />
      <meta property="og:title" content={shareTitle} />
      <meta property="og:description" content={text} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={img} />
      <meta name="twitter:title" content={shareTitle} />
      <meta name="twitter:description" content={text} />
      <meta name="twitter:image" content={img} />
    </Helmet>
  );
}

/** Private screens (account, checkout, dashboards, auth) stay out of search results. */
export function NoIndex() {
  return (
    <Helmet>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
  );
}

/** Home page structured data: who we are, and the sitelinks search box (WebSite + SearchAction). */
export function SiteJsonLd() {
  const base = SITE || "https://realitystore.com.ng";
  const data = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "RealityStore",
      url: base,
      logo: `${base}/icons/icon-512.png`,
      description: DEFAULT_DESCRIPTION,
      areaServed: "NG",
      slogan: "Where we bring all your dreams to reality",
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "RealityStore",
      url: base,
      potentialAction: {
        "@type": "SearchAction",
        target: { "@type": "EntryPoint", urlTemplate: `${base}/search?q={search_term_string}` },
        "query-input": "required name=search_term_string",
      },
    },
  ];
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
}
